import React, { type FC, useCallback, useEffect, useState } from 'react';
import { connect, type MapStateToProps, useDispatch, useSelector } from 'react-redux';
import { compose } from '@reduxjs/toolkit';

import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { useModulesScrollbarContext } from 'app/design-system/components/Scrollbar/ModulesScrollbarContext';
import { asyncQueue } from 'app/lib/asyncQueue';
import { MatchType } from 'app/lib/betting/betting-entities';
import { selectSignalrConnected } from 'app/lib/signalr';

import {
  PREMATCH_MATCHES_TOPIC,
  subscribeToPrematchMatches,
  unsubscribeFromPrematchMatches,
} from '../../../../PrematchWS/actions';
import { MatchRowStatus } from '../../../constants';
import MatchDetails from '../../MatchDetails';
import InnerLineContainer from './InnerLines';
import MatchLineButtons from './MatchLineButtons';
import RightCellDefault from './RightCellDefault';
import RightCellHeadToHead from './RightCellHeadToHead';

import styles from './MatchRow.module.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BettingContextProps = any;

type StoreProps = {
  betCount: number;
  matchType: MatchType;
  selectedRow: boolean;
};

type MatchRowWSProps = {
  matchId: number;
};

const MatchRowWS: FC<MatchRowWSProps & StoreProps> = ({
  betCount,
  matchId,
  matchType,
  selectedRow,
}) => {
  const dispatch = useDispatch();
  const scrollbarContext = useModulesScrollbarContext();
  const signalrConnected = useSelector(selectSignalrConnected);
  const [expansion, setExpansion] = useState(MatchRowStatus.COLLAPSED);
  const [scrollTopByMatchId, setScrollTopByMatchId] = useState(new Map());
  const isHeadToHead = matchType === MatchType.HEAD_TO_HEAD;
  const isExpanded = expansion === MatchRowStatus.EXPANDED && selectedRow;

  const expandClickHandler = useCallback(() => {
    if (selectedRow && expansion === MatchRowStatus.EXPANDED) {
      setExpansion(MatchRowStatus.COLLAPSED);
    }
    else {
      signalrConnected && asyncQueue.addToQueue(
        `${PREMATCH_MATCHES_TOPIC}#${matchId}`,
        () => dispatch(subscribeToPrematchMatches(`${matchId}`, [matchId])),
      );
      setExpansion(MatchRowStatus.LOADING);
    }

    const scrollRef = scrollbarContext?.scrollbarRef;
    const contentWrapperEl = scrollRef?.current?.contentWrapperEl;

    if (contentWrapperEl) {
      const { scrollTop } = contentWrapperEl;

      if (expansion === MatchRowStatus.COLLAPSED) {
        setScrollTopByMatchId(scrollTopByMatchId.set(matchId, scrollTop));
      }
      else {
        contentWrapperEl.scrollTo({ top: scrollTopByMatchId.get(matchId) });
        setScrollTopByMatchId(new Map());
      }
    }
  }, [expansion, selectedRow, matchId, signalrConnected]);

  useEffect(() => {
    if (selectedRow && expansion === MatchRowStatus.LOADING) {
      setExpansion(MatchRowStatus.EXPANDED);
    }

    if (!selectedRow && expansion === MatchRowStatus.EXPANDED) {
      setExpansion(MatchRowStatus.COLLAPSED);
    }
  }, [selectedRow, expansion]);

  useEffect(() => {
    return () => {
      if (isExpanded) {
        asyncQueue.removeFromQueue(
          `${PREMATCH_MATCHES_TOPIC}#${matchId}`,
          () => dispatch(unsubscribeFromPrematchMatches(`${matchId}`)),
        );
      }
    };
  }, [isExpanded]);

  return (
    <div className={styles.matchRowContent}>
      <div className={styles.matchRow}
           data-test={`match-row-${matchId}`}>
        <div className={styles.detailsCell}>
          <MatchDetails matchId={matchId} />
        </div>

        {isHeadToHead ? (
          <RightCellHeadToHead matchId={matchId}
                               betCount={betCount}
                               onClick={expandClickHandler} />
        ) : (
          <div data-test="match-row-default-header-title">
            <RightCellDefault expanded={isExpanded}
                              isHeadToHead={isHeadToHead} />
          </div>
        )}

        {Boolean(betCount) && (
          <MatchLineButtons matchId={matchId}
                            betCount={betCount}
                            matchType={matchType}
                            expansionState={expansion}
                            onClick={expandClickHandler} />
        )}
      </div>
      <div data-test={`inner-line-container-${matchId}`}
           className={styles.innerLineContainer}>
        {isExpanded ? (
          <InnerLineContainer matchId={matchId} />
        ) : null}
      </div>
    </div>
  );
};

const createMapStateToProps: MapStateToProps<
  StoreProps,
  BettingContextProps,
  ReduxState
> = (state, {
  $betting: {
    content: {
      selectors: {
        matches,
      },
    },
    matchContent: {
      selectors: {
        matchIds,
      },
    },
  },
  matchId,
}) => {
  const matchesState = matches(state);
  const match = matchesState.get(matchId);
  const matchType = matchesState.getIn([matchId, 'type'], MatchType.HEAD_TO_HEAD);
  const prematchMatchIds = matchIds(state);
  const selectedRow = prematchMatchIds.includes(matchId);

  return ({
    betCount: match?.betsCount || 0,
    matchType,
    selectedRow,
  });
};

export default compose<FC<MatchRowWSProps>>(
  withinBettingContext,
  connect(createMapStateToProps),
)(MatchRowWS);
